import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { cn } from "../../lib/utils"

// Figma squircle 마스크 SVG를 data URI로 인코딩 (avatar-mask.svg의 path)
const SQUIRCLE_MASK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cpath d='M0.011 20.002C0.011 20.002-0.503 30.481 5.519 35.47C8.827 38.211 12.614 40.051 20.002 39.989C20.002 39.989 30.481 40.508 35.47 34.486C38.211 31.177 40.051 27.39 39.989 20.002C39.989 20.002 40.508 9.523 34.486 4.534C31.177 1.794 27.39-0.051 20.002 0.011C20.002 0.011 9.519-0.503 4.534 5.519C1.794 8.845-0.051 12.619 0.011 20.002Z' fill='white'/%3E%3C/svg%3E")`

// Figma 기본 아바타 (squircle 배경 + user-corner 아이콘) - 인라인으로 렌더링하여 이미지 로딩 이슈 방지
const FigmaDefaultAvatar = React.memo(function FigmaDefaultAvatar() {
    return (
        <div className="relative w-full h-full">
            {/* squircle 배경 (#eceff2 in light, #2d3239 in dark) */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.011 20.002C0.011 20.002-0.503 30.481 5.519 35.47C8.827 38.211 12.614 40.051 20.002 39.989C20.002 39.989 30.481 40.508 35.47 34.486C38.211 31.177 40.051 27.39 39.989 20.002C39.989 20.002 40.508 9.523 34.486 4.534C31.177 1.794 27.39-0.051 20.002 0.011C20.002 0.011 9.519-0.503 4.534 5.519C1.794 8.845-0.051 12.619 0.011 20.002Z" className="fill-[rgb(var(--avatar-bg))]"/>
            </svg>
            {/* user-corner 아이콘 (중앙 62.5% 영역 = 18.75% inset) */}
            <svg className="absolute inset-[18.75%] w-[62.5%] h-[62.5%]" viewBox="0 0 18.75 20.83" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M4.688 4.688C4.688 2.099 6.786 0 9.375 0C11.964 0 14.063 2.099 14.063 4.688C14.063 7.276 11.964 9.375 9.375 9.375C6.786 9.375 4.688 7.276 4.688 4.688ZM11.099 10.417C12.887 10.41 14.589 11.148 15.953 12.451C17.313 13.751 18.271 15.546 18.724 17.543C18.832 18.018 18.596 18.505 18.156 18.714C10.102 22.542 3.094 20.345 0.475 18.648C0.108 18.41-0.071 17.968 0.026 17.541C0.484 15.544 1.446 13.751 2.806 12.452C4.172 11.15 5.874 10.412 7.663 10.417H11.099Z" className="fill-[rgb(var(--avatar-icon))]"/>
            </svg>
            {/* squircle 스트로크 (테두리) */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.868 0.507C19.906 0.508 19.934 0.508 19.952 0.509C19.961 0.509 19.968 0.51 19.973 0.51C19.974 0.51 19.976 0.511 19.977 0.511H20.681C23.998 0.537 26.531 0.969 28.609 1.693C30.823 2.465 32.545 3.576 34.166 4.919C37.044 7.303 38.39 11.032 39.005 14.233C39.311 15.825 39.432 17.266 39.477 18.309C39.499 18.829 39.501 19.249 39.499 19.538C39.498 19.682 39.495 19.794 39.493 19.868C39.492 19.905 39.491 19.934 39.49 19.952C39.49 19.961 39.489 19.967 39.489 19.972C39.489 19.974 39.489 19.977 39.489 19.978L39.488 19.992L39.489 20.006C39.52 23.658 39.08 26.393 38.309 28.609C37.538 30.823 36.428 32.545 35.085 34.166C32.701 37.044 28.972 38.39 25.771 39.005C24.179 39.311 22.739 39.432 21.696 39.477C21.176 39.499 20.755 39.501 20.466 39.499C20.322 39.498 20.21 39.495 20.136 39.493C20.099 39.492 20.071 39.491 20.053 39.49C20.044 39.49 20.037 39.489 20.032 39.489H20.026L20.013 39.488L19.998 39.489C16.346 39.52 13.611 39.08 11.395 38.309C9.181 37.538 7.459 36.428 5.838 35.085C2.96 32.701 1.613 28.972 0.997 25.771C0.691 24.179 0.57 22.738 0.524 21.695C0.502 21.175 0.499 20.755 0.501 20.466C0.502 20.322 0.505 20.21 0.507 20.136C0.508 20.099 0.508 20.07 0.509 20.052C0.509 20.043 0.51 20.037 0.51 20.032C0.51 20.03 0.511 20.028 0.511 20.027V19.324C0.537 16.009 0.969 13.48 1.693 11.403C2.465 9.192 3.576 7.468 4.92 5.837L4.919 5.836C7.301 2.958 11.03 1.613 14.231 0.997C15.824 0.691 17.265 0.57 18.308 0.524C18.829 0.502 19.249 0.499 19.538 0.501C19.682 0.502 19.794 0.505 19.868 0.507Z" stroke="rgb(var(--avatar-stroke-color, 227 230 234))"/>
            </svg>
        </div>
    )
})

const Avatar = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & {
        /** true이면 squircle 마스크 대신 원형(rounded-full) 사용 (1:1 채팅 등) */
        useCircle?: boolean;
    }
>(({ className, useCircle, ...props }, ref) => {
    const isCircle = useCircle || className?.includes('rounded-full')
    return (
        <AvatarPrimitive.Root
            ref={ref}
            className={cn(
                "relative flex h-10 w-10 shrink-0",
                isCircle ? "overflow-hidden rounded-full" : "overflow-visible",
                className
            )}
            style={isCircle ? undefined : {
                WebkitMaskImage: SQUIRCLE_MASK,
                maskImage: SQUIRCLE_MASK,
                WebkitMaskSize: '100% 100%',
                maskSize: '100% 100%',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
            }}
            {...props}
        />
    )
})
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Image>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, ...props }, ref) => {
    return (
        <AvatarPrimitive.Image
            ref={ref}
            src={src}
            className={cn("aspect-square h-full w-full", className)}
            {...props}
        />
    )
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Fallback>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & {
        /** Figma 기본 아바타 사용 (squircle 배경 + user-corner 아이콘) */
        useFigmaDefault?: boolean;
    }
>(({ className, useFigmaDefault = true, children, ...props }, ref) => (
    <AvatarPrimitive.Fallback
        ref={ref}
        className={cn(
            "flex h-full w-full items-center justify-center",
            !useFigmaDefault && "rounded-[rgb(var(--radius-avatar))] bg-muted",
            className
        )}
        {...props}
    >
        {useFigmaDefault ? <FigmaDefaultAvatar /> : children}
    </AvatarPrimitive.Fallback>
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

// 스트로크 오버레이 — 아바타 이미지 위에 squircle 테두리를 표시
// Avatar 컴포넌트 바깥에서 사용: <div className="relative"><Avatar>...</Avatar><AvatarStroke /></div>
const AvatarStroke = React.memo(function AvatarStroke({ className }: { className?: string }) {
    return (
        <svg
            className={cn("absolute inset-0 w-full h-full pointer-events-none", className)}
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M19.868 0.507C19.906 0.508 19.934 0.508 19.952 0.509C19.961 0.509 19.968 0.51 19.973 0.51C19.974 0.51 19.976 0.511 19.977 0.511H20.681C23.998 0.537 26.531 0.969 28.609 1.693C30.823 2.465 32.545 3.576 34.166 4.919C37.044 7.303 38.39 11.032 39.005 14.233C39.311 15.825 39.432 17.266 39.477 18.309C39.499 18.829 39.501 19.249 39.499 19.538C39.498 19.682 39.495 19.794 39.493 19.868C39.492 19.905 39.491 19.934 39.49 19.952C39.49 19.961 39.489 19.967 39.489 19.972C39.489 19.974 39.489 19.977 39.489 19.978L39.488 19.992L39.489 20.006C39.52 23.658 39.08 26.393 38.309 28.609C37.538 30.823 36.428 32.545 35.085 34.166C32.701 37.044 28.972 38.39 25.771 39.005C24.179 39.311 22.739 39.432 21.696 39.477C21.176 39.499 20.755 39.501 20.466 39.499C20.322 39.498 20.21 39.495 20.136 39.493C20.099 39.492 20.071 39.491 20.053 39.49C20.044 39.49 20.037 39.489 20.032 39.489H20.026L20.013 39.488L19.998 39.489C16.346 39.52 13.611 39.08 11.395 38.309C9.181 37.538 7.459 36.428 5.838 35.085C2.96 32.701 1.613 28.972 0.997 25.771C0.691 24.179 0.57 22.738 0.524 21.695C0.502 21.175 0.499 20.755 0.501 20.466C0.502 20.322 0.505 20.21 0.507 20.136C0.508 20.099 0.508 20.07 0.509 20.052C0.509 20.043 0.51 20.037 0.51 20.032C0.51 20.03 0.511 20.028 0.511 20.027V19.324C0.537 16.009 0.969 13.48 1.693 11.403C2.465 9.192 3.576 7.468 4.92 5.837L4.919 5.836C7.301 2.958 11.03 1.613 14.231 0.997C15.824 0.691 17.265 0.57 18.308 0.524C18.829 0.502 19.249 0.499 19.538 0.501C19.682 0.502 19.794 0.505 19.868 0.507Z" stroke="rgb(var(--avatar-stroke-color, 227 230 234))"/>
        </svg>
    )
})

export { Avatar, AvatarImage, AvatarFallback, AvatarStroke, FigmaDefaultAvatar }
